import {useRouteLoaderData} from '@github-ui/react-router'

import type {RouteQueryKey} from '../query-key'
import type {AnyQueryRoute, RouteMatchData} from './query-route'

/**
 * Returns the {@link RouteQueryKey} for a named query on the given route, or
 * `undefined` if the route is not currently mounted.
 *
 * This reads directly from the (serializable) loader data emitted by
 * {@link QueryRoute}'s loader. Prefer this over building a `queryConfig`
 * via `useQueriesConfig` when all you need is the key (e.g. for
 * `queryClient.getQueryData(queryKey)` or `invalidateQueries({queryKey})`).
 *
 * Use cases:
 * - Looking up cached data via `queryClient.getQueryData(queryKey)` from a
 *   component that lives above (or beside) the owning route, where calling
 *   `useRouteQuery` isn't appropriate.
 */
export function useRouteQueryKey<Config extends AnyQueryRoute, QueryName extends string & keyof Config['queries']>(
  route: Config,
  queryName: QueryName,
): RouteQueryKey | undefined {
  const data = useRouteLoaderData<RouteMatchData<Config> | undefined>(route.id)
  return data?.queries?.[queryName]?.queryKey
}

/** The named parts of a {@link RouteQueryKey}, in order, minus its trailing `queryDeps`. */
const QUERY_KEY_SEGMENTS = ['appName', 'routeId', 'routePath', 'queryName'] as const

type RouteQueryKeySegment = (typeof QUERY_KEY_SEGMENTS)[number]

/**
 * Returns a {@link RouteQueryKey} truncated after `through`, or `undefined` if the route
 * is not currently mounted. Defaults to dropping only the trailing `queryDeps`.
 *
 * Useful if you want to invalidate multiple queryKeys with a matching prefix.
 *
 * Example use cases:
 * - Refreshing filtered list results after an update. If your list is filtered by search params, then you need to invalidate
 *   the queryKey without search params so any previously used search params don't return stale data.
 */
export function useRouteQueryKeyPrefix<
  Config extends AnyQueryRoute,
  QueryName extends string & keyof Config['queries'],
>(route: Config, queryName: QueryName, through: RouteQueryKeySegment = 'queryName'): string[] | undefined {
  const queryKey = useRouteQueryKey(route, queryName)
  if (!queryKey) return undefined
  const [appName, routeId, routePath, name] = queryKey
  return [appName, routeId, routePath, name].slice(0, QUERY_KEY_SEGMENTS.indexOf(through) + 1)
}
